import React, { useRef, useEffect } from "react";
import type { CVData, WorkEntry, EducationEntry } from "@/lib/cvParser";

// ── Editable text primitive ──────────────────────────────────────────────────
interface EditableTextProps {
  value: string;
  onUpdate: (v: string) => void;
  style?: React.CSSProperties;
  multiline?: boolean;
}

const EditableText = ({ value, onUpdate, style, multiline = false }: EditableTextProps) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current && ref.current.innerText !== value) {
      ref.current.innerText = value;
    }
  }, [value]);

  const handleBlur = () => {
    if (ref.current) {
      const v = ref.current.innerText.trim();
      if (v !== value) onUpdate(v);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!multiline && e.key === "Enter") {
      e.preventDefault();
      ref.current?.blur();
    }
  };

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      style={{ outline: "none", cursor: "text", ...style }}
    >
      {value}
    </div>
  );
};

// ── Section heading: blue accent ON TOP of the grey full-width divider ───────
const SectionHeading = ({ children, onUpdate }: { children: string; onUpdate?: (v: string) => void }) => (
  <div style={{ marginBottom: "16px" }}>
    {onUpdate ? (
      <EditableText
        value={children}
        onUpdate={onUpdate}
        style={{
          fontSize: "16px",
          fontWeight: 700,
          color: "#1a3a6b",
          fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
          letterSpacing: "0.2px",
          display: "inline-block",
          marginBottom: "6px",
        }}
      />
    ) : (
      <span style={{
        fontSize: "16px",
        fontWeight: 700,
        color: "#1a3a6b",
        fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
        letterSpacing: "0.2px",
        display: "inline-block",
        marginBottom: "6px",
      }}>{children}</span>
    )}
    {/* Blue accent overlapping the grey full-width rule */}
    <div style={{ height: "1px", backgroundColor: "#e2e8f0", position: "relative", marginTop: "4px" }}>
      <div style={{ 
        position: "absolute", 
        left: 0, 
        top: "-1px", 
        width: "40px", 
        height: "3px", 
        backgroundColor: "#2563eb", 
        borderRadius: "2px" 
       }} />
    </div>
  </div>
);

// ── Icon SVGs (inline, no dependency) ─────────────────────────────────────
const IconLocation = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5a7fa8" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
  </svg>
);

const IconMail = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5a7fa8" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
  </svg>
);

const IconPhone = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5a7fa8" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.18 19.79 19.79 0 0 1 1.61 4.6 2 2 0 0 1 3.6 2.42h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.17 6.17l1.87-1.87a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const IconCalendar = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5a7fa8" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const IconCert = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <circle cx="12" cy="8" r="6" /><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11" />
  </svg>
);

const IconPerson = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5a7fa8" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);

// ── Debounce helper ───────────────────────────────────────────────────────
function debounce<T extends (...args: any[]) => void>(fn: T, wait: number) {
  let t: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
}

// ── Component interface ───────────────────────────────────────────────────
interface VanguardTemplateProps {
  data: CVData;
  onUpdate?: (newData: CVData) => void;
}

// ── Main template ─────────────────────────────────────────────────────────
const VanguardTemplate = React.forwardRef<HTMLDivElement, VanguardTemplateProps>(
  ({ data, onUpdate }, ref) => {

    const debouncedUpdate = React.useCallback(
      debounce((newData: CVData) => { if (onUpdate) onUpdate(newData); }, 300),
      [onUpdate]
    );

    const updH = (key: "name" | "jobTitle", val: string) => debouncedUpdate({ ...data, [key]: val });

    const updSection = (key: keyof NonNullable<CVData["sectionNames"]>, val: string) =>
      debouncedUpdate({ ...data, sectionNames: { ...(data.sectionNames || {}), [key]: val } as any });

    const updBasic = (i: number, val: string) => {
      const a = [...data.basicInfo]; a[i] = val; debouncedUpdate({ ...data, basicInfo: a });
    };
    const updProfile = (val: string) => debouncedUpdate({ ...data, profile: val });

    const updWork = (i: number, field: keyof WorkEntry, val: any) => {
      const a = [...data.workExperience]; a[i] = { ...a[i], [field]: val };
      debouncedUpdate({ ...data, workExperience: a });
    };
    const updWorkBullet = (wi: number, bi: number, val: string) => {
      const a = [...data.workExperience];
      const b = [...a[wi].bullets]; b[bi] = val; a[wi] = { ...a[wi], bullets: b };
      debouncedUpdate({ ...data, workExperience: a });
    };
    const updEdu = (i: number, field: keyof EducationEntry, val: any) => {
      const a = [...data.education]; a[i] = { ...a[i], [field]: val };
      debouncedUpdate({ ...data, education: a });
    };
    const updSkill = (i: number, val: string) => {
      const a = [...data.keySkills]; a[i] = val; debouncedUpdate({ ...data, keySkills: a });
    };
    const updInterest = (i: number, val: string) => {
      const a = [...data.interests]; a[i] = val; debouncedUpdate({ ...data, interests: a });
    };

    const sNames = data.sectionNames || {
      basicInfo: "Contact",
      profile: "Professional Summary",
      keySkills: "Skills",
      workExperience: "Work Experience",
      education: "Education",
      interests: "Languages",
      references: "Certifications",
    };

    // Parse out contact info from basicInfo array
    const contactItems = data.basicInfo;

    // ── Language proficiency map (based on interest labels) ──────────────
    const languageLevels: Record<string, number> = {
      native: 100, fluent: 85, advanced: 70, intermediate: 55, beginner: 30, basic: 30,
    };
    const parseLanguage = (item: string): { lang: string; level: string; pct: number } => {
      const [lang, level] = item.split(/\s*[–\-•|]\s*/);
      const lvl = (level || "native").toLowerCase().trim();
      return { lang: lang?.trim() || item, level: level?.trim() || "Native", pct: languageLevels[lvl] ?? 100 };
    };

    const baseFont = "'Helvetica Neue', Helvetica, Arial, sans-serif";

    return (
      <div
        ref={ref}
        id="cv-template-root"
        style={{
          backgroundColor: "#ffffff",
          color: "#1e293b",
          fontFamily: baseFont,
          width: "794px",
          minHeight: "1122px",
          boxSizing: "border-box",
          margin: "0 auto",
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        }}
      >
        {/* CSS Injection for Vanguard PDF specifically */}
        <style dangerouslySetInnerHTML={{ __html: `
          /* Force the generator's hidden wrapper to have zero padding so Vanguard fits perfectly */
          body > div[style*="top: -99999px"] {
            padding: 0 !important;
          }
          /* Website-matching horizontal padding - Reduced to 44px for PDF to fit 'managing' on line 1 */
          body > #cv-template-root {
            padding-left: 44px !important;
            padding-right: 44px !important;
          }
          /* Micro-adjustment: Ensure left column has enough space for 1-to-1 wrapping parity */
          body > #cv-template-root > div[style*="display: flex"] > div:first-child {
            flex: 0 0 65% !important;
          }
          /* USER FIX: Center icons and text precisely */
          body > #cv-template-root .vanguard-contact-item,
          body > #cv-template-root .vanguard-date-row,
          body > #cv-template-root .vanguard-references-row {
            display: flex !important;
            align-items: center !important;
            min-height: 16px !important;
          }
          /* Nudge icons UP slightly to align with text baseline/center */
          body > #cv-template-root svg {
            width: 12px !important;
            height: 12px !important;
            margin-right: 6px !important;
            vertical-align: middle !important;
            display: inline-block !important;
            flex-shrink: 0 !important;
            position: relative !important;
            top: -1px !important; /* Nudge up */
          }
          /* SKILL PILLS: Use the 'Classic' box approach (padding-bottom nudge) */
          body > #cv-template-root .vanguard-skill-pill {
            display: inline-block !important;
            padding: 0px 12px 8px 12px !important; /* 0 top, 8px bottom = nudge text UP */
            line-height: 1 !important;
            vertical-align: middle !important;
            background-color: #f1f5f9 !important;
            border-radius: 20px !important;
            height: 22px !important;
            margin-bottom: 8px !important;
            margin-right: 8px !important;
          }
          body > #cv-template-root .vanguard-skill-pill * {
            line-height: 1 !important;
            vertical-align: middle !important;
            display: inline !important;
          }
          /* GLOBAL BOTTOM PROTECTION */
          body > #cv-template-root {
            padding-bottom: 60px !important;
          }
        `}} />

        {/* ── HEADER ──────────────────────────────────────────────────── */}
        <div style={{ padding: "20px 48px 20px" }}>
          <EditableText
            value={data.name}
            onUpdate={(v) => updH("name", v)}
            style={{ fontSize: "30px", fontWeight: 800, color: "#0f1e3d", letterSpacing: "-0.5px", display: "block", lineHeight: 1.15 }}
          />
          <EditableText
            value={data.jobTitle}
            onUpdate={(v) => updH("jobTitle", v)}
            style={{ fontSize: "15px", fontWeight: 400, color: "#64748b", marginTop: "4px", display: "block" }}
          />

          {/* Contact grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 24px", marginTop: "16px" }}>
            {contactItems.map((item, i) => {
              const lower = item.toLowerCase();
              let Icon: React.FC = IconPerson;
              // Prioritize phone if keywords exist
              if (lower.includes("mobile") || lower.includes("phone") || lower.includes("tel:") || (!!lower.match(/\+?\d[\d\s\-()]{8,}/) && !lower.includes("@"))) {
                Icon = IconPhone;
              } else if (lower.includes("@")) {
                Icon = IconMail;
              } else if (lower.includes("dob") || lower.includes("birth") || lower.includes("date") || lower.includes("license") || lower.includes("licence")) {
                Icon = IconCalendar;
              } else if (lower.includes(",") || /\b(street|road|ave|avenue|lane|drive|london|paris|berlin|stockholm|new york|los angeles|sydney|dubai|singapore|gothenburg|manchester|edinburgh|birmingham|bristol)\b/.test(lower)) {
                Icon = IconLocation;
              }

              return (
                <div key={i} className="vanguard-contact-item" style={{ display: "flex", alignItems: "flex-start", gap: "6px", minHeight: "18px" }}>
                  <Icon />
                  <EditableText
                    value={item}
                    onUpdate={(v) => updBasic(i, v)}
                    style={{ fontSize: "11px", color: "#475569", lineHeight: "1.4", flex: 1 }}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Full-width divider */}
        <div style={{ height: "1px", backgroundColor: "#e2e8f0", margin: "0 48px" }} />

        {/* ── BODY: two columns ─────────────────────────────────────── */}
        <div style={{ display: "flex", padding: "28px 48px 40px", gap: "36px" }}>

          {/* LEFT — 65% */}
          <div style={{ flex: "0 0 63%", minWidth: 0 }}>

            {/* Professional Summary */}
            <div style={{ marginBottom: "28px" }}>
              <SectionHeading onUpdate={(v) => updSection("profile", v)}>{sNames.profile}</SectionHeading>

              <EditableText
                value={data.profile}
                onUpdate={updProfile}
                multiline
                style={{ fontSize: "12px", color: "#334155", lineHeight: "1.65" }}
              />
            </div>

            {/* Work Experience */}
            <div style={{ marginBottom: "28px" }}>
              <SectionHeading onUpdate={(v) => updSection("workExperience", v)}>{sNames.workExperience}</SectionHeading>

              {data.workExperience.map((entry, i) => (
                <div key={i} className="vanguard-work-entry" style={{ marginBottom: "22px" }}>
                  {/* Job title row */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "3px" }}>
                    <EditableText
                      value={entry.jobTitle}
                      onUpdate={(v) => updWork(i, "jobTitle", v)}
                      style={{ fontSize: "13px", fontWeight: 600, color: "#2563eb" }}
                    />
                    <div className="vanguard-date-row" style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <IconCalendar />
                      <EditableText
                        value={entry.dates}
                        onUpdate={(v) => updWork(i, "dates", v)}
                        style={{ fontSize: "11px", color: "#64748b", whiteSpace: "nowrap" }}
                      />
                    </div>
                  </div>
                  {/* Company + location row */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "8px" }}>
                    <EditableText
                      value={entry.company}
                      onUpdate={(v) => updWork(i, "company", v)}
                      style={{ fontSize: "12px", fontWeight: 700, color: "#0f1e3d" }}
                    />
                  </div>
                  {/* Bullets */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                    {entry.bullets.map((b, bi) => (
                      <div key={bi} style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                        <span style={{ fontSize: "11px", color: "#475569", lineHeight: "1.6", marginTop: "1px" }}>–</span>
                        <EditableText
                          value={b}
                          onUpdate={(v) => updWorkBullet(i, bi, v)}
                          multiline
                          style={{ fontSize: "12px", color: "#334155", lineHeight: "1.6", flex: 1 }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Education */}
            <div>
              <SectionHeading onUpdate={(v) => updSection("education", v)}>{sNames.education}</SectionHeading>

              {data.education.map((entry, i) => (
                <div key={i} className="vanguard-education-entry" style={{ marginBottom: "18px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "3px" }}>
                    <EditableText
                      value={entry.qualification}
                      onUpdate={(v) => updEdu(i, "qualification", v)}
                      multiline
                      style={{ fontSize: "13px", fontWeight: 600, color: "#2563eb", flex: 1, marginRight: "12px" }}
                    />
                    <div className="vanguard-date-row" style={{ display: "flex", alignItems: "center", gap: "5px", flexShrink: 0 }}>
                      <IconCalendar />
                      <EditableText
                        value={entry.dates}
                        onUpdate={(v) => updEdu(i, "dates", v)}
                        style={{ fontSize: "11px", color: "#64748b", whiteSpace: "nowrap" }}
                      />
                    </div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <EditableText
                      value={entry.institution}
                      onUpdate={(v) => updEdu(i, "institution", v)}
                      style={{ fontSize: "12px", color: "#0f1e3d", fontWeight: 400 }}
                    />
                    {entry.notes && entry.notes.length > 0 && (
                      <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        <IconLocation />
                        <EditableText
                          value={entry.notes[0]}
                          onUpdate={(v) => {
                            const newNotes = [...entry.notes]; newNotes[0] = v;
                            updEdu(i, "notes", newNotes);
                          }}
                          style={{ fontSize: "11px", color: "#64748b" }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — 35% */}
          <div style={{ flex: "0 0 calc(37% - 36px)", minWidth: 0 }}>

            {/* Certifications — using references field */}
            <div style={{ marginBottom: "28px" }}>
              <SectionHeading onUpdate={(v) => updSection("references", v)}>{sNames.references}</SectionHeading>

              {/* Render as free-text but styled */}
              <div style={{ fontSize: "11px", color: "#334155", lineHeight: "1.6" }}>
                <div className="vanguard-references-row" style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                  <IconCert />
                  <EditableText
                    value={data.references || "Add your certifications here"}
                    onUpdate={(v) => debouncedUpdate({ ...data, references: v })}
                    multiline
                    style={{ fontSize: "11px", color: "#334155", lineHeight: "1.6", flex: 1 }}
                  />
                </div>
              </div>
            </div>

            {/* Skills — pill / badge style */}
            <div style={{ marginBottom: "28px" }}>
              <SectionHeading onUpdate={(v) => updSection("keySkills", v)}>{sNames.keySkills}</SectionHeading>

              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {data.keySkills.map((skill, i) => (
                  <div
                    key={i}
                    className="vanguard-skill-pill"
                    style={{
                      backgroundColor: "#f1f5f9",
                      border: "1px solid #e2e8f0",
                      borderRadius: "20px",
                      padding: "4px 12px",
                      fontSize: "11px",
                      color: "#1e293b",
                      fontWeight: 500,
                      cursor: "text",
                    }}
                  >
                    <EditableText
                      value={skill}
                      onUpdate={(v) => updSkill(i, v)}
                      style={{ fontSize: "11px", color: "#1e293b" }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Languages — progress bars */}
            <div>
              <SectionHeading onUpdate={(v) => updSection("interests", v)}>{sNames.interests}</SectionHeading>

              <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                {data.interests.map((item, i) => {
                  const hasDash = /\s*[–\-]\s*/.test(item);
                  const { lang, level, pct } = parseLanguage(item);
                  return (
                    <div key={i}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "5px" }}>
                        <EditableText
                          value={lang}
                          onUpdate={(v) => {
                            updInterest(i, hasDash ? `${v} - ${level}` : v);
                          }}
                          style={{ fontSize: "12px", fontWeight: 600, color: "#0f1e3d" }}
                        />
                        {hasDash && (
                          <EditableText
                            value={level}
                            onUpdate={(v) => {
                              updInterest(i, `${lang} - ${v}`);
                            }}
                            style={{ fontSize: "11px", color: "#64748b" }}
                          />
                        )}
                      </div>
                      {/* Progress bar — only for language entries */}
                      {hasDash && (
                        <div style={{ height: "5px", backgroundColor: "#e2e8f0", borderRadius: "3px", overflow: "hidden" }}>
                          <div
                            style={{
                              height: "100%",
                              width: `${pct}%`,
                              backgroundColor: "#1a3a6b",
                              borderRadius: "3px",
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}

              </div>
            </div>

          </div>
        </div>
        <div style={{ height: "60px", clear: "both" }} />
      </div>
    );
  }
);

VanguardTemplate.displayName = "VanguardTemplate";
(VanguardTemplate as any).templateName = "Vanguard";

export default VanguardTemplate;
